import { watch } from 'vue';
import { defineNuxtPlugin } from '#app';
import { useConfig } from '~/composables/useConfig';

type JimoQueue = Array<[string, string, (string | number | object | boolean)[]]>;

export default defineNuxtPlugin(() => {
  if (import.meta.server) return;

  if (typeof navigator !== 'undefined' && navigator.webdriver) {
    return;
  }

  const config = useConfig();

  const initJimo = (publicConfig: Record<string, unknown>) => {
    const projectId =
      typeof publicConfig.jimoProjectId === 'string' ? publicConfig.jimoProjectId.trim() : '';

    if (
      !projectId ||
      projectId === 'undefined' ||
      projectId === 'null' ||
      typeof window === 'undefined' ||
      window.jimo != null
    )
      return;

    window.jimo = [] as JimoQueue;
    window.JIMO_PROJECT_ID = projectId;

    const identifyUser = () => {
      const userId = localStorage.getItem('userId') || undefined;
      if (!userId) return;

      const username = localStorage.getItem('username') || undefined;

      window.jimo?.push([
        'do',
        'identify',
        [
          userId,
          () => {
            if (username) window.jimo?.push(['set', 'user:name', [username]]);
          }
        ]
      ]);
    };

    identifyUser();
    window.addEventListener('jimo:identify', identifyUser);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://undercity.usejimo.com/jimo-invader.js';
    document.head.appendChild(script);

    const setHeight = (px: number) =>
      document.documentElement.style.setProperty('--jimo-banner-height', `${px}px`);

    let currentShadowRoot: ShadowRoot | null = null;
    let shadowMutationObserver: MutationObserver | null = null;
    let resizeObserver: ResizeObserver | null = null;

    const updateHeight = (shadowRoot: ShadowRoot) => {
      const topBar = shadowRoot.querySelector<HTMLElement>('.top-bar-wrapper');
      const isVisible = !!topBar && topBar.offsetHeight > 0;
      setHeight(isVisible ? topBar.offsetHeight : 0);

      if (isVisible && !resizeObserver) {
        resizeObserver = new ResizeObserver(() => updateHeight(shadowRoot));
        resizeObserver.observe(topBar);
      } else if (!isVisible && resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
    };

    const observeShadowRoot = (shadowRoot: ShadowRoot) => {
      shadowMutationObserver?.disconnect();
      resizeObserver?.disconnect();
      resizeObserver = null;

      updateHeight(shadowRoot);

      shadowMutationObserver = new MutationObserver(() => updateHeight(shadowRoot));
      shadowMutationObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    };

    const checkJimoContainer = () => {
      const container = document.getElementById('jimo_container');
      if (!container) return;

      const shadowRoot = container.shadowRoot;
      if (shadowRoot) {
        if (shadowRoot !== currentShadowRoot) {
          currentShadowRoot = shadowRoot;
          observeShadowRoot(shadowRoot);
        }
      } else {
        requestAnimationFrame(checkJimoContainer);
      }
    };

    const observer = new MutationObserver(checkJimoContainer);
    observer.observe(document.body, { childList: true, subtree: true });

    checkJimoContainer();
  };

  watch(
    () => config.value?.public,
    (publicConfig) => {
      if (!publicConfig) return;
      initJimo(publicConfig as unknown as Record<string, unknown>);
    },
    { immediate: true }
  );
});
